/**
 * Story 049 — Dashboard landing page.
 *
 * Mounts at `/` (was `/data-acquisitions` redirect pre-049). Page shell
 * follows the canonical apps/web rhythm: outer Box flex column with
 * pinned PageHeading + action bar + (optional) warning banner, then a
 * `flex: 1` region hosting the widget canvas.
 *
 * UX brief §1 — page shell flex pattern: outer Box height 100%, the
 * RGL container is the only scroll container.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { DashboardWorkspace } from "./DashboardWorkspace.js";
import {
  useDashboardLayoutMutation,
  useDashboardLayoutQuery,
} from "./queries/useDashboardLayout.js";
import {
  ALL_WIDGETS,
  WIDGET_TITLE,
  type DashboardLayoutEnvelope,
  type WidgetKey,
} from "./shared/factoryDefaults.js";
import { RotateCcwIcon, SettingsIcon } from "@aeriosiq/ui/icons";

export function DashboardPage(): ReactElement {
  const { envelope, isFetchError, isPending, refetch } =
    useDashboardLayoutQuery();
  const {
    saveLayout,
    isError: isSaveError,
    errorDetail: saveErrorDetail,
  } = useDashboardLayoutMutation();

  const resetRef = useRef<(() => void) | null>(null);
  const restoreRef = useRef<((key: WidgetKey) => void) | null>(null);
  const hiddenRef = useRef<Record<string, boolean>>({});

  // Snapshot of the hidden set used by the gear menu's render — we
  // bump a counter every time the menu opens so the menu sees the
  // current state without re-rendering the whole page on every gesture.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTick, setMenuTick] = useState(0);
  const menuOpen = menuAnchor !== null;

  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  // R58-W-07 — freeze the detail at the moment isError flips true so the
  // toast keeps its message even after a subsequent successful PUT flips
  // mutation.error back to null. Without this freeze, the Snackbar would
  // re-render mid-display with "—" or a stale label as the mutation state
  // races a follow-up gesture's success.
  const [frozenDetail, setFrozenDetail] = useState<string | null>(null);
  // Only persist once the GET has AUTHORITATIVELY resolved (a 200 with the
  // saved envelope, or a genuine 404 "no saved layout"). While the query is
  // pending or errored, the page optimistically shows factory defaults — and
  // react-grid-layout fires `onLayoutChange` on mount. Persisting then would
  // PUT those defaults over the user's real saved layout in the DB. That is
  // exactly what wiped saved layouts: a transient load 400/401 → defaults
  // shown → mount-time persist → saved arrangement overwritten with defaults.
  // `isFetchError` covers 4xx/5xx; `isPending` covers the not-yet-loaded window
  // (incl. the tenant-readiness gate from the GET fix).
  const canPersist = !isPending && !isFetchError;
  const handlePersist = (next: DashboardLayoutEnvelope): void => {
    if (canPersist) saveLayout(next);
  };

  // Wire a toast for PUT failures. The mutation hook re-emits its
  // error state every render; capture the latest into a Snackbar.
  // `useEffect` (not `useMemo`) — side effects must not run during
  // render; React 19 strict mode invokes `useMemo` factories twice
  // (UX brief #3, QA B-049-Q5).
  const showSaveError = isSaveError;
  useEffect(() => {
    if (showSaveError) {
      setFrozenDetail(saveErrorDetail);
      setSaveErrorOpen(true);
    }
  }, [showSaveError, saveErrorDetail]);

  const hiddenKeys = useMemo(() => {
    void menuTick; // re-evaluate when the menu re-opens
    return ALL_WIDGETS.filter((k) => hiddenRef.current[k] === true);
  }, [menuTick]);

  const handleReset = (): void => {
    resetRef.current?.();
  };

  const handleRestore = (key: WidgetKey): void => {
    restoreRef.current?.(key);
    // Close the menu so focus returns to the gear button.
    setMenuAnchor(null);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexShrink: 0, mb: 1, gap: 2 }}
      >
        <Box>
          <PageHeading>Dashboard</PageHeading>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Your KPIs and entry points at a glance.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            onClick={handleReset}
            startIcon={<RotateCcwIcon fontSize="small" />}
            aria-label="Reset layout"
          >
            Reset layout
          </Button>
          <IconButton
            aria-label="Workspace settings"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              setMenuAnchor(e.currentTarget);
              setMenuTick((n) => n + 1);
            }}
            size="small"
            sx={{ width: 32, height: 32 }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/*
        Page-level inline warning banner. Only rendered when the GET
        resolved with a 5xx (AC12). Copy is literal per the story's
        §"Persistence contract".
        Per UX brief §5.2 this is a non-blocking banner — the user
        keeps interacting with the defaulted layout — so `role="status"`
        + `aria-live="polite"` (NOT `role="alert"`, which is rude /
        interrupts the user's flow). The visible body text is the
        accessible name; no `aria-label` override needed.
        The Snackbar approach used elsewhere doesn't work here — the
        warning needs to be persistent until the next successful GET,
        not auto-hide after 4 s.
      */}
      {isFetchError && !isPending ? (
        <Alert
          severity="warning"
          variant="outlined"
          role="status"
          aria-live="polite"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                refetch();
              }}
            >
              Retry
            </Button>
          }
          sx={{ flexShrink: 0, mb: 1 }}
        >
          Couldn&apos;t load your saved dashboard layout. Showing defaults.
        </Alert>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <DashboardWorkspace
          initialEnvelope={envelope}
          onPersist={handlePersist}
          resetSignal={{ ref: resetRef }}
          restoreSignal={{ ref: restoreRef }}
          hiddenRef={hiddenRef}
        />
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => {
          setMenuAnchor(null);
        }}
        // MUI's `<Menu>` ships with canonical `role="menu"` semantics
        // (and individual `<MenuItem>`s expose `role="menuitem"`); the
        // acceptance spec is updated in parallel to scope its Restore
        // lookup via `getByRole("menu")`. No `role="dialog"` override
        // on the paper — that was a test-affordance hack flagged by UX.
        MenuListProps={{
          dense: true,
          "aria-label": "Workspace settings",
        }}
        slotProps={{
          paper: {
            sx: { minWidth: 240 },
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
          >
            Hidden widgets
          </Typography>
        </Box>
        {hiddenKeys.length === 0 ? (
          <Box sx={{ px: 1.5, py: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              All widgets are visible.
            </Typography>
          </Box>
        ) : (
          hiddenKeys.map((k) => (
            <MenuItem
              key={k}
              dense
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography variant="body2">{WIDGET_TITLE[k]}</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  handleRestore(k);
                }}
                aria-label={`Restore ${WIDGET_TITLE[k]}`}
              >
                Restore
              </Button>
            </MenuItem>
          ))
        )}
        <MenuItem
          dense
          onClick={() => {
            handleReset();
            setMenuAnchor(null);
          }}
          sx={{ mt: 0.5 }}
        >
          <RotateCcwIcon fontSize="small" aria-hidden sx={{ mr: 1 }} />
          Reset layout
        </MenuItem>
      </Menu>

      <Snackbar
        open={saveErrorOpen}
        autoHideDuration={5000}
        onClose={() => {
          setSaveErrorOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => {
            setSaveErrorOpen(false);
          }}
        >
          {/* R58-W-07 — AC3 detail-aware copy. Em-dash separates the
              user-facing label from the BE's `message` / `code` / status
              fallback. The detail comes from the mutation's
              `extractSaveErrorDetail`. */}
          {frozenDetail
            ? `Couldn’t save dashboard layout — ${frozenDetail}`
            : "Couldn’t save dashboard layout."}
        </Alert>
      </Snackbar>
    </Box>
  );
}
